import { useCallback, useEffect, useRef } from 'react'
import { markActivity, IDLE_MS } from '@/services/auth/sessionActivity'

// IDLE_MS(50分) の 5 分前に警告。WARNING_GRACE_MS(5分) と整合させること。
const WARN_MS = 45 * 60 * 1000

const EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'] as const

interface Options {
  onWarn: () => void
  onIdle: () => void
  enabled: boolean
}

export function useIdleTimeout({ onWarn, onIdle, enabled }: Options): () => void {
  const warnRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const idleRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const onWarnRef = useRef(onWarn)
  const onIdleRef = useRef(onIdle)
  // 最新コールバックの差し替えは描画中ではなく effect で行う（描画中の ref 書き込みを避ける）。
  // タイマ発火は数十分後なので、描画後反映でも挙動は変わらない。
  useEffect(() => {
    onWarnRef.current = onWarn
    onIdleRef.current = onIdle
  })

  const reset = useCallback(() => {
    if (warnRef.current) clearTimeout(warnRef.current)
    if (idleRef.current) clearTimeout(idleRef.current)
    warnRef.current = setTimeout(() => onWarnRef.current(), WARN_MS)
    idleRef.current = setTimeout(() => onIdleRef.current(), IDLE_MS)
    markActivity()
  }, [])

  const lastResetAt = useRef(0)
  const throttledReset = useCallback(() => {
    const now = Date.now()
    if (now - lastResetAt.current < 1000) return
    lastResetAt.current = now
    reset()
  }, [reset])

  useEffect(() => {
    if (!enabled) {
      if (warnRef.current) clearTimeout(warnRef.current)
      if (idleRef.current) clearTimeout(idleRef.current)
      return
    }

    reset()
    EVENTS.forEach((e) => window.addEventListener(e, throttledReset, { passive: true }))
    return () => {
      if (warnRef.current) clearTimeout(warnRef.current)
      if (idleRef.current) clearTimeout(idleRef.current)
      EVENTS.forEach((e) => window.removeEventListener(e, throttledReset))
    }
  }, [enabled, reset, throttledReset])

  return reset
}
